import { memo, useEffect, useRef, useState } from "react";
import { OfficeEditProps } from "./types";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { initialOfficeEditForm } from "services/api/offices/consts";
import { OFFICE_EDIT_FORM_SCHEMA } from "./validation";
import {
  createOfficeAPI,
  getOfficeAPI,
  updateOfficeAPI,
} from "services/api/offices/api";
import { Office, OfficeEditFormValues } from "services/api/offices/types";
import { transformOfficeEditFormValues } from "services/api/offices/transformations";
import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import OfficeInfoStep from "./components/OfficeInfoStep/OfficeInfoStep";
import OfficeAddressStep from "./components/OfficeAddressStep/OfficeAddressStep";
import OfficeEmailStep from "./components/OfficeEmailStep/OfficeEmailStep";
import OfficePhoneStep from "./components/OfficePhoneStep/OfficePhoneStep";
import OfficeUserStep from "./components/OfficeUserStep/OfficeUserStep";
import OfficeNoteStep from "./components/OfficeNoteStep/OfficeNoteStep";
import OfficeSummaryStep from "./components/OfficeSummaryStep/OfficeSummaryStep";
import OfficeStepStatusBar from "./components/OfficeStepStatusBar/OfficeStepStatusBar";
import OfficeIntro from "./components/OfficeIntro/OfficeIntro";

const OfficeEditCard: React.FC<OfficeEditProps> = (props) => {
  const { officeId, show, onSuccess, onClose, isFullScreen = true, tenantId } = props;

  const [formValues, setFormValues] = useState<OfficeEditFormValues>(initialOfficeEditForm);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [stepCurrent, setStepCurrent] = useState(0);
  const showIntro = isFullScreen;
  const title = officeId ? "Ažuriranje poslovnice" : "Unos poslovnice";
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (show && officeId) {
      fetchOffice(officeId);
    }
    if (show && !officeId) {
      setFormValues(initialOfficeEditForm);
      setStepCurrent(0);
      setErrors({});
    }
  }, [show, officeId]);

  const fetchOffice = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getOfficeAPI(id);
      setFormValues(transformOfficeEditFormValues(result));
      setErrors({});
    } catch {
      // error handled by snackbar
    }
    setIsLoading(false);
  };

  const saveOffice = async () => {
    setIsLoading(true);
    try {
      const id = officeId || formValues.id;
      let result: Office | undefined;

      if (!!id) {
        result = await updateOfficeAPI(id, formValues);
      } else {
        result = await createOfficeAPI(formValues, tenantId);
      }

      if (result) {
        setFormValues((prev) => ({ ...prev, id: result!.id }));
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleNextStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      if (stepCurrent === 0) {
        const isValid = validateYup(formValues, OFFICE_EDIT_FORM_SCHEMA, setErrors);
        if (!isValid) return;
        await saveOffice();
      }
      setStepCurrent((s) => s + 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleBackStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      setStepCurrent((s) => s - 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleFinish = async () => {
    const id = officeId || formValues.id;
    if (id && onSuccess) {
      setFormValues(initialOfficeEditForm);
      setStepCurrent(0);
      onSuccess(id);
    }
  };

  const stepLabels = ["Naziv", "Adrese", "Mailovi", "Telefoni", "Korisnici", "Bilješke", "Pregled"];

  const handleStepClick = (stepIndex: number) => {
    setStepCurrent(stepIndex);
  };

  const firstStepCompleted = !!(officeId || formValues.id);

  const statusBar = (
    <OfficeStepStatusBar
      stepLabels={stepLabels}
      stepCurrent={stepCurrent}
      onStepClick={handleStepClick}
      firstStepCompleted={firstStepCompleted}
    />
  );

  const steps = [
    <WizardStep key="info" stepNumber={0} stepCurrent={stepCurrent}>
      {statusBar}
      <OfficeInfoStep formValues={formValues} setFormValues={setFormValues} errors={errors} setErrors={setErrors} />
    </WizardStep>,
    <WizardStep key="addresses" stepNumber={1} stepCurrent={stepCurrent}>
      {statusBar}
      <OfficeAddressStep formValues={formValues} />
    </WizardStep>,
    <WizardStep key="emails" stepNumber={2} stepCurrent={stepCurrent}>
      {statusBar}
      <OfficeEmailStep formValues={formValues} />
    </WizardStep>,
    <WizardStep key="phones" stepNumber={3} stepCurrent={stepCurrent}>
      {statusBar}
      <OfficePhoneStep formValues={formValues} />
    </WizardStep>,
    <WizardStep key="people" stepNumber={4} stepCurrent={stepCurrent}>
      {statusBar}
      <OfficeUserStep formValues={formValues} tenantId={tenantId} />
    </WizardStep>,
    <WizardStep key="notes" stepNumber={5} stepCurrent={stepCurrent}>
      {statusBar}
      <OfficeNoteStep formValues={formValues} />
    </WizardStep>,
    <WizardStep key="summary" stepNumber={6} stepCurrent={stepCurrent}>
      {statusBar}
      <OfficeSummaryStep formValues={formValues} />
    </WizardStep>,
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      size={isFullScreen ? "full" : "medium"}
      position={isFullScreen ? "side" : "centered"}
      intro={isFullScreen && showIntro ? {
        content: (
          <OfficeIntro
            formValues={formValues}
            isEdit={!!officeId}
            stepCurrent={stepCurrent}
            stepCount={steps.length}
          />
        ),
      } : undefined}>
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={title}
        onHide={onClose} />
      <ModalBody>
        <Wizard steps={steps} />
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(OfficeEditCard);
